import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import styles from './index.module.scss';
import { pollingParams } from '@/services/fetchs';

interface TipItem {
  showMsg?: string;
  sendMsg?: string;
  question?: string;
  extend?: any;
}

interface SlidingTipsProps {
  tips?: (TipItem | string)[];
  tempMessage?: TipItem | string;
  onClick?: (sendMsg: string, extend?: any) => void;
}

let tipsIntervalTime: NodeJS.Timeout | undefined; // 轮循展示tips的定时器
let tipsItemIntervalTime: NodeJS.Timeout | undefined; // 展示tips中每一项的定时器

let _maxPollingCount = -1; // 最大轮询次数，-1表示不限制
let _questionPollingInterval = 5000; // 问题轮询间隔时间，5秒
let _queuePollingInterval = 5 * 60 * 1000; // (问题项)队列轮询间隔时间，5分钟

const SlidingTips: React.FC<SlidingTipsProps> = ({ tips = [], tempMessage, onClick }) => {
  const [msg, setMsg] = useState<TipItem | string | boolean | undefined>();

  // 获取播报消息-轮询配置参数
  useEffect(() => {
    pollingParams().then(res => {
      const { success, data } = res;
      if (success && data) {
        const { maxPollingCount, questionPollingInterval, queuePollingInterval } = data;
        if (maxPollingCount) {
          _maxPollingCount = maxPollingCount;
        }
        if (questionPollingInterval) {
          _questionPollingInterval = questionPollingInterval;
        }
        if (queuePollingInterval) {
          _queuePollingInterval = queuePollingInterval;
        }
      }
    });
  }, []);

  /**
   * 每间隔 5 分钟，执行一次【轮循展示tips】，
   */
  const onIntervalTimeTips = (): void => {
    if (!tips || tips?.length <= 0) {
      return;
    }
    tipsIntervalTime && clearInterval(tipsIntervalTime);
    tipsIntervalTime = setInterval(() => {
      showTips();
    }, _queuePollingInterval);
  };

  /**
   * 轮循展示tips中每一项各5000ms, 展示一轮后结束
   */
  const showTips = (): void => {
    if (!tips || tips?.length <= 0) {
      return;
    }
    tipsItemIntervalTime && clearInterval(tipsItemIntervalTime);
    setMsg(tips[0]);
    let index = 0;
    const timerId = setInterval(() => {
      index += 1;
      if (index >= tips.length) {
        // 先判断再 setMsg，避免 index === tips.length 时 setMsg(tips[index]) 取到 undefined
        clearInterval(timerId);
        tipsItemIntervalTime = undefined;
        setMsg(false);
        onIntervalTimeTips();
      } else {
        setMsg(tips[index]);
      }
    }, _questionPollingInterval);
    tipsItemIntervalTime = timerId;
  };

  /**
   * 当注入tempMessage时，立即展示tempMessage, 且 10000ms后隐藏并执行【轮循展示tips】
   */
  const showTempMessage = (): void => {
    if (tempMessage) {
      setMsg(tempMessage);
      setTimeout(() => {
        setMsg(undefined);
        onIntervalTimeTips();
      }, 10000);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    tipsIntervalTime && clearInterval(tipsIntervalTime);
    tipsItemIntervalTime && clearInterval(tipsItemIntervalTime);
    showTips();
  }, [tips]);
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(showTempMessage, []);

  const sendMsg = useCallback(
    // eslint-disable-next-line no-shadow
    (sendMsg: string, extend?: any) => {
      sendMsg && onClick?.(sendMsg, extend);
    },
    [onClick],
  );

  const getMsgDisplay = (msg: TipItem | string | boolean | undefined): string => {
    if (typeof msg === 'string') return msg;
    if (typeof msg === 'object' && msg?.showMsg) return msg.showMsg;
    return '';
  };

  const getMsgSend = (msg: TipItem | string | boolean | undefined): string | undefined => {
    if (typeof msg === 'object' && (msg?.sendMsg || msg?.question)) return msg.sendMsg || msg.question;
    return undefined;
  };

  return msg ? (
    <div className={styles['sliding-tips-container']}>
      <div className={styles['sliding-tips']}>
        <div
          className={cx(styles.tip, { [styles.bolder]: getMsgSend(msg) })}
          onClick={() => {
            const sendMessage = getMsgSend(msg);
            if (sendMessage) {
              const extend = typeof msg === 'object' && msg !== null && 'extend' in msg ? (msg as any).extend : undefined;
              sendMsg(sendMessage, extend);
            }
          }}
        >
          <img
            className={styles.start}
            src="https://imgcdn.sto.cn/C02B5EFD-B52D-45-36x27-start-color.png"
            alt=""
          />
          {getMsgDisplay(msg)}
        </div>
      </div>
    </div>
  ) : null;
};

export default SlidingTips; 